// Takeoff Canvas 2.0 A3 — multi-sheet usability over the EXISTING
// TakeoffDrawingSheets rows (roadmap A3: "no new drawing-sheet table").
// Pure helpers the canvas's Sheets dock section, Manage Sheets dialog and
// side-by-side compare pane share: ordering, next / previous, the calibration
// status a sheet card shows, sheet number + discipline metadata (db/38
// SheetNumber / Discipline, 2026-09-14) and a best-effort parse of both from
// an uploaded file's name so "A-101 First Floor Plan.pdf" lands pre-filled —
// always editable, never authoritative.

export const SHEET_DISCIPLINES = Object.freeze([
  { key: "arch",    label: "Architectural", prefixes: ["A", "AD", "ID"] },
  { key: "mech",    label: "Mechanical",    prefixes: ["M", "MH", "H"] },
  { key: "elec",    label: "Electrical",    prefixes: ["E", "EL"] },
  { key: "plumb",   label: "Plumbing",      prefixes: ["P", "PL"] },
  { key: "process", label: "Process",       prefixes: ["PR", "PP", "Q"] },
  { key: "fire",    label: "Fire protection", prefixes: ["FP", "F", "FA"] },
  { key: "struct",  label: "Structural",    prefixes: ["S", "ST"] },
  { key: "civil",   label: "Civil / site",  prefixes: ["C", "CS", "L"] },
  { key: "general", label: "General",       prefixes: ["G", "T"] },
  { key: "other",   label: "Other",         prefixes: [] },
]);
export const disciplineLabel = (key) => SHEET_DISCIPLINES.find(d => d.key === key)?.label || (key ? String(key) : "");

export const orderedSheets = (sheets = []) => [...sheets].sort((a, b) => ((a.DisplayOrder ?? 100) - (b.DisplayOrder ?? 100)) || ((a.SheetID ?? 0) - (b.SheetID ?? 0)));

// Next (+1) / previous (−1) sheet in display order, wrapping; null with < 2 sheets.
export function adjacentSheetId(sheets, activeId, dir = 1) {
  const o = orderedSheets(sheets);
  if (o.length < 2) return null;
  const i = Math.max(0, o.findIndex(s => s.SheetID === activeId));
  return o[(i + (dir >= 0 ? 1 : o.length - 1)) % o.length].SheetID;
}

// "A-101 · First Floor Plan" — the number when the estimator entered one.
export const sheetTitle = (s) => s ? `${s.SheetNumber ? `${s.SheetNumber} · ` : ""}${s.SheetName || `Sheet ${s.SheetID}`}` : "";

// Calibration status from the sheet's own scale fields (Track B verifies a
// scale against a printed dimension; this only says whether one exists).
//   { status: 'none' | 'pdf' | 'raster', ftPerInch?, ftPerPx?, label }
export function sheetCalibration(s) {
  if (!s) return { status: "none", label: "no drawing" };
  if (!s.BackgroundImage) return { status: "none", label: "no drawing" };
  if (s.ImageIsPdf && s.ImageFtPerInch > 0) return { status: "pdf", ftPerInch: +s.ImageFtPerInch, label: `${fmtFtPerInch(+s.ImageFtPerInch)} · calibrated` };
  if (!s.ImageIsPdf && s.ImageScaleFtPerPx > 0) return { status: "raster", ftPerPx: +s.ImageScaleFtPerPx, label: "calibrated (raster)" };
  return { status: "none", label: "scale not set" };
}
function fmtFtPerInch(v) {
  // 4 → 1/4" = 1'-0"; 8 → 1/8"; non-standard → "1 in = 12.5 ft"
  const std = { 32: '1/32"', 16: '1/16"', 8: '1/8"', 4: '1/4"', 2: '1/2"', 1: '1"' };
  for (const [k, lbl] of Object.entries(std)) if (Math.abs(v - +k) / +k < 0.005) return `${lbl} = 1'-0"`;
  return `1 in = ${Math.round(v * 100) / 100} ft`;
}

// Thumbnail cache key: the image identity, the page, and the raster/PDF flag.
export const sheetThumbKey = (s) => s ? `${s.SheetID}:${s.ImagePage ?? 1}:${+s.ImageRotation || 0}:${s.ImageIsPdf ? "pdf" : "img"}:${(s.BackgroundImage || "").length}` : "";

// Best-effort "A-101" + discipline from a file / sheet name. Returns
// { sheetNumber, discipline, name } where name is the remainder for the sheet
// name; nothing matched → nulls + the input trimmed. Never more than a hint.
export function parseSheetMetaFromName(raw) {
  // strip only a real file extension — "M-2.01 - HVAC Plan" must keep its ".01"
  const text = String(raw || "").replace(/\.(pdf|png|jpe?g|gif|tiff?|webp|bmp|dwg|dxf)$/i, "").replace(/[_]+/g, " ").trim();
  const m = text.match(/^([A-Z]{1,2})[-\s]?(\d{1,3}(?:\.\d{1,2})?[A-Z]?)\b[\s\-–—:]*(.*)$/i);
  if (!m) return { sheetNumber: null, discipline: null, name: text };
  const letters = m[1].toUpperCase();
  const number = `${letters}-${m[2].toUpperCase()}`;
  const disc = SHEET_DISCIPLINES.find(d => d.prefixes.includes(letters))?.key || null;
  const rest = m[3].trim();
  return { sheetNumber: number, discipline: disc, name: rest || text };
}

// Page numbering ACROSS drawings: a revision can hold several files (each a PDF with its own pages, or a single image /
// stored-set page). `counts` maps SheetID -> that drawing's page count; a drawing not counted yet (or a raster) is 1 page.
const countOf = (counts, id) => Math.max(1, Number(counts?.[id]) || 1);

/** Where the working page sits among every page of every drawing, in display order: { position, total }. */
export function pagePosition(sheets, counts, activeId, pageNum = 1) {
  let before = 0, total = 0;
  for (const s of orderedSheets(sheets)) {
    const n = countOf(counts, s.SheetID);
    if (s.SheetID === activeId) before = total;
    total += n;
  }
  const n = countOf(counts, activeId);
  return { position: before + Math.min(Math.max(1, pageNum || 1), n), total };
}

/** The page one step from here: within the drawing, else the first / last page of the neighbouring drawing. null = at the end. */
export function stepPage(sheets, counts, activeId, pageNum = 1, dir = 1) {
  const n = countOf(counts, activeId), want = (pageNum || 1) + dir;
  if (want >= 1 && want <= n) return { sheetId: activeId, page: want };
  const list = orderedSheets(sheets), i = list.findIndex(s => s.SheetID === activeId), nb = list[i + dir];
  if (i < 0 || !nb) return null;
  return { sheetId: nb.SheetID, page: dir > 0 ? 1 : countOf(counts, nb.SheetID) };
}

/** The inverse of pagePosition: which drawing + page is overall page `n` (clamped to 1..total)? */
export function pageAt(sheets, counts, n) {
  const list = orderedSheets(sheets);
  if (!list.length) return null;
  let left = Math.max(1, Math.floor(Number(n)) || 1);
  for (const s of list) {
    const c = countOf(counts, s.SheetID);
    if (left <= c) return { sheetId: s.SheetID, page: left };
    left -= c;
  }
  const last = list[list.length - 1];
  return { sheetId: last.SheetID, page: countOf(counts, last.SheetID) };
}

// Key sheets: a big package keeps every drawing, but the few that matter get a star (TakeoffDrawingSheets.IsKeySheet);
// a drawing with takeoff on it is "key" too, derived from the rows themselves so nothing has to be kept in step.
/** Takeoff per drawing: { [SheetID]: { rooms, conditions } } — rows with no drawing (tray rooms, manual lines) are not counted, back-outs are not conditions. */
export function sheetTakeoffCounts(rooms = [], conditions = []) {
  const out = {};
  const bump = (id, k) => { if (id == null) return; (out[id] ||= { rooms: 0, conditions: 0 })[k] += 1; };
  for (const r of rooms) bump(r.SheetID, "rooms");
  for (const c of conditions) if (c.MeasureRole !== "deduct") bump(c.SheetID, "conditions");
  return out;
}

export const hasTakeoff = (counts, id) => !!counts?.[id] && (counts[id].rooms > 0 || counts[id].conditions > 0);

/** "3 rooms · 5 cond." — "" when nothing is drawn on the drawing. */
export function takeoffBadgeText(counts, id) {
  const c = counts?.[id];
  if (!c || (!c.rooms && !c.conditions)) return "";
  return [c.rooms ? `${c.rooms} room${c.rooms === 1 ? "" : "s"}` : "", c.conditions ? `${c.conditions} cond.` : ""].filter(Boolean).join(" · ");
}

/** The drawings worth jumping to — starred, or with takeoff on them — in display order. */
export const keySheets = (sheets, counts) => orderedSheets(sheets).filter(s => s.IsKeySheet || hasTakeoff(counts, s.SheetID));
